import React, { useEffect, useState } from 'react';
import {
   Film, Video, Camera, Clapperboard, Play, MonitorPlay, Aperture,
   Sparkles, TrendingUp, Users, CheckCircle2, ChevronDown, ChevronUp,
   Star, Award, Briefcase, Building2, HeartPulse, GraduationCap,
   Cpu, ShoppingCart, Factory, ArrowRight, PlayCircle, Quote,
   Check, Clock, Zap, ShieldCheck, Globe, Activity, Smartphone,
   Utensils, Store, Dumbbell, Stethoscope, Lightbulb, Package, BookOpen
} from 'lucide-react';
import SEO from './SEO';

const FAQItem = ({ question, answer }) => {
   const [isOpen, setIsOpen] = useState(false);
   return (
      <div className="border border-white/10 rounded-2xl bg-[#0F0F16] overflow-hidden mb-4 transition-all duration-300 hover:border-blue-500/30">
         <button
            onClick={() => setIsOpen(!isOpen)}
            className="w-full px-6 py-5 flex items-center justify-between font-semibold text-lg text-left text-white group"
         >
            <span className="group-hover:text-blue-400 transition-colors pr-8">{question}</span>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${isOpen ? 'bg-blue-500/20 text-blue-400 rotate-180' : 'bg-white/5 text-gray-400'}`}>
               <ChevronDown className="w-5 h-5" />
            </div>
         </button>
         <div className={`px-6 overflow-hidden transition-all duration-500 ease-in-out ${isOpen ? 'max-h-40 pb-5 opacity-100' : 'max-h-0 opacity-0'}`}>
            <p className="text-gray-400 text-lg leading-relaxed">{answer}</p>
         </div>
      </div>
   );
};

const VideoProduction = () => {
   useEffect(() => {
      window.scrollTo(0, 0);
   }, []);

   return (
      <div className="bg-[#05050A] min-h-screen text-gray-200 pt-24 overflow-hidden selection:bg-blue-500/30 selection:text-white" style={{ fontFamily: 'emoji' }}>
         <SEO
           title="Video Production Services in India – Vishwa DigiTech"
           description="Professional video production services by Vishwa DigiTech. We create corporate videos, product videos, social media reels, ads & brand films. High-quality video content that drives engagement."
           keywords="video production sangli, corporate video making india, product video sangli, social media reels production, brand film india, video editing services maharashtra"
           canonicalPath="/services/video-production"
         />

         {/* Background Animated Gradient */}
         <div className="fixed inset-0 z-0 pointer-events-none opacity-20">
            <div className="absolute top-[-10%] left-[-10%] w-[50vw] h-[50vw] bg-blue-600/30 rounded-full blur-[150px] mix-blend-screen animate-pulse" style={{ animationDuration: '8s' }} />
            <div className="absolute bottom-[-10%] right-[-10%] w-[50vw] h-[50vw] bg-purple-600/20 rounded-full blur-[150px] mix-blend-screen animate-pulse" style={{ animationDuration: '10s' }} />
         </div>

         {/* 1. Hero Banner */}
         <section className="relative pt-20 pb-32 lg:pt-32 lg:pb-40 px-4 sm:px-6 lg:px-8 z-10">
            <div className="max-w-7xl mx-auto flex flex-col items-center text-center">
               <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full border border-blue-500/30 bg-blue-500/10 backdrop-blur-md mb-8 hover:bg-blue-500/20 transition-all cursor-default">
                  <Video className="w-4 h-4 text-blue-400" />
                  <span className="text-sm font-bold tracking-widest text-blue-300 uppercase">Professional Video Production Services</span>
               </div>

               <h1 className="text-5xl md:text-7xl lg:text-8xl font-black mb-8 leading-[1.1] tracking-tight">
                  Create Powerful Videos That <br className="hidden md:block" />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-indigo-400 to-purple-400">Inspire, Engage & Grow</span>
               </h1>

               <p className="text-xl md:text-2xl text-gray-400 mb-12 leading-relaxed max-w-4xl font-light">
                  Bring your brand to life with high-quality promotional videos, corporate films, product videos, social media reels, advertisements, and creative visual storytelling. At Vishwa DigiTech, we create videos that capture attention and deliver results.
               </p>

               <div className="flex flex-col sm:flex-row gap-5 w-full sm:w-auto justify-center">
                  <button className="px-10 py-5 rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white font-bold text-lg shadow-[0_0_40px_rgba(59,130,246,0.3)] hover:-translate-y-1 transition-all">
                     Get Free Consultation
                  </button>
                  <button className="px-10 py-5 rounded-2xl border border-white/20 bg-white/5 hover:bg-white/10 text-white font-bold text-lg transition-all backdrop-blur-sm flex items-center justify-center gap-3 group">
                     Start Your Project <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </button>
               </div>
            </div>
         </section>

         {/* 2. Why Video Production Matters */}
         <section className="py-24 border-t border-white/5 bg-[#0A0A12] relative z-10">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
               <div className="grid lg:grid-cols-2 gap-16 items-center">
                  <div className="relative rounded-[2rem] overflow-hidden border border-white/10 shadow-2xl group h-full min-h-[400px]">
                     <div className="absolute inset-0 bg-blue-500/20 mix-blend-overlay opacity-0 group-hover:opacity-100 transition-opacity duration-700 z-10 pointer-events-none" />
                     <video
                        autoPlay
                        loop
                        muted
                        playsInline
                        className="absolute inset-0 w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-1000"
                     >
                        <source src="https://videos.pexels.com/video-files/3130284/3130284-uhd_2560_1440_30fps.mp4" type="video/mp4" />
                        Your browser does not support the video tag.
                     </video>
                  </div>

                  <div className="space-y-8">
                     <h2 className="text-4xl md:text-5xl font-bold">Why Does Your Business Need <span className="text-blue-400">Professional Videos?</span></h2>
                     <div className="p-6 rounded-2xl bg-gradient-to-r from-blue-900/30 to-purple-900/10 border-l-4 border-blue-500">
                        <p className="text-xl text-white mb-2 leading-relaxed">आजच्या डिजिटल युगात लोक टेक्स्टपेक्षा व्हिडिओ जास्त पाहतात.</p>
                        <p className="text-gray-300">एक आकर्षक आणि व्यावसायिक व्हिडिओ तुमच्या ब्रँडला विश्वासार्हता देतो, ग्राहकांचे लक्ष वेधतो आणि विक्री वाढवण्यास मदत करतो.</p>
                     </div>

                     <div className="grid sm:grid-cols-2 gap-4">
                        {[
                           "Increase Brand Awareness",
                           "Improve Customer Engagement",
                           "Boost Sales & Conversions",
                           "Build Brand Trust",
                           "Perfect for Social Media",
                           "Professional Presentation"
                        ].map((item, idx) => (
                           <div key={idx} className="flex items-center gap-3 p-4 bg-white/5 rounded-xl border border-white/5 hover:border-blue-500/30 transition-colors">
                              <CheckCircle2 className="w-5 h-5 text-blue-400 shrink-0" />
                              <span className="font-semibold text-gray-200">{item}</span>
                           </div>
                        ))}
                     </div>
                  </div>
               </div>
            </div>
         </section>

         {/* 3. Our Video Production Services */}
         <section className="py-32 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="text-center mb-20">
               <h2 className="text-4xl md:text-5xl font-bold mb-6">Our Video <span className="text-blue-400">Production Services</span></h2>
               <p className="text-xl text-gray-400 max-w-3xl mx-auto">Comprehensive video solutions crafted to elevate your brand's digital presence.</p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
               {[
                  { title: "Corporate Videos", icon: Building2 },
                  { title: "Promotional Videos", icon: TrendingUp },
                  { title: "Product Videos", icon: Package },
                  { title: "Social Media Reels", icon: Smartphone },
                  { title: "YouTube Videos", icon: MonitorPlay },
                  { title: "Brand Story Videos", icon: BookOpen },
                  { title: "Testimonial Videos", icon: Quote },
                  { title: "Event Coverage", icon: Sparkles },
                  { title: "Drone Videography", icon: Aperture },
                  { title: "Real Estate Videos", icon: Building2 },
                  { title: "Educational Videos", icon: GraduationCap },
                  { title: "Animated Explainer", icon: Clapperboard }
               ].map((service, idx) => (
                  <div key={idx} className="group p-8 rounded-3xl bg-[#0F0F16] border border-white/5 hover:border-blue-500/50 hover:bg-white/[0.03] transition-all duration-300 hover:-translate-y-2 cursor-pointer flex flex-col items-center text-center">
                     <div className="w-16 h-16 rounded-full bg-blue-500/10 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                        {typeof service.icon === 'function' ? service.icon() : <service.icon className="w-8 h-8 text-blue-400" />}
                     </div>
                     <h3 className="text-xl font-bold text-gray-200 group-hover:text-white">{service.title}</h3>
                  </div>
               ))}
            </div>
         </section>

         {/* 4. Our Production Process */}
         <section className="py-32 bg-[#0A0A12] border-y border-white/5 relative z-10">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
               <div className="text-center mb-20">
                  <h2 className="text-4xl md:text-5xl font-bold mb-6">Our Production <span className="text-indigo-400">Process</span></h2>
                  <p className="text-xl text-gray-400 max-w-3xl mx-auto">From concept to final delivery, we follow a streamlined approach to ensure the highest quality results.</p>
               </div>

               <div className="relative">
                  {/* Desktop Horizontal Line */}
                  <div className="hidden lg:block absolute top-1/2 left-0 w-full h-1 bg-white/10 -translate-y-1/2" />

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-16">
                     {[
                        { step: "1", title: "Project Discussion", desc: "Understanding goals & audience." },
                        { step: "2", title: "Script Writing", desc: "Crafting the perfect message." },
                        { step: "3", title: "Storyboarding", desc: "Visualizing the video flow." },
                        { step: "4", title: "Video Shooting", desc: "High-end cinematic capture." },
                        { step: "5", title: "Editing & Motion", desc: "Adding effects & graphics." },
                        { step: "6", title: "Color Grading", desc: "Enhancing visual appeal." },
                        { step: "7", title: "Audio Mixing", desc: "Sound design & voiceovers." },
                        { step: "8", title: "Final Delivery", desc: "Ready to publish formats." }
                     ].map((item, idx) => (
                        <div key={idx} className="relative z-10 flex flex-col items-center text-center group">
                           <div className="w-20 h-20 rounded-full bg-[#05050A] border-2 border-indigo-500/50 flex items-center justify-center text-3xl font-black text-indigo-400 mb-6 group-hover:bg-indigo-500 group-hover:text-white group-hover:scale-110 transition-all shadow-[0_0_20px_rgba(99,102,241,0.2)]">
                              {item.step}
                           </div>
                           <h3 className="text-xl font-bold mb-2 text-white">{item.title}</h3>
                           <p className="text-sm text-gray-400">{item.desc}</p>
                        </div>
                     ))}
                  </div>
               </div>
            </div>
         </section>

         {/* 5. Why Choose Vishwa DigiTech & 8. Benefits */}
         <section className="py-32 relative z-10">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
               <div className="grid lg:grid-cols-2 gap-20">

                  {/* Why Choose Us */}
                  <div>
                     <h2 className="text-4xl font-bold mb-10">Why Choose <br /><span className="text-blue-400">Vishwa DigiTech?</span></h2>
                     <div className="space-y-4">
                        {[
                           "Creative Video Concepts", "Professional Equipment", "4K Video Quality",
                           "Cinematic Editing", "Motion Graphics", "Fast Project Delivery",
                           "Affordable Pricing", "Experienced Team", "Social Media Optimized", "Client Satisfaction"
                        ].map((feature, idx) => (
                           <div key={idx} className="flex items-center gap-4 p-4 rounded-xl bg-[#0F0F16] border border-white/5 hover:border-blue-500/30 transition-colors">
                              <div className="w-8 h-8 rounded-full bg-blue-500/10 flex items-center justify-center shrink-0">
                                 <Check className="w-5 h-5 text-blue-400" />
                              </div>
                              <span className="font-semibold text-gray-200 text-lg">{feature}</span>
                           </div>
                        ))}
                     </div>
                  </div>

                  {/* Benefits */}
                  <div>
                     <h2 className="text-4xl font-bold mb-10">Benefits of Professional <br /><span className="text-purple-400">Video Production</span></h2>
                     <div className="grid sm:grid-cols-2 gap-4">
                        {[
                           { title: "Higher Engagement", icon: Users },
                           { title: "Better Brand Recognition", icon: Award },
                           { title: "More Website Traffic", icon: Globe },
                           { title: "Increased Sales", icon: TrendingUp },
                           { title: "Professional Image", icon: Briefcase },
                           { title: "Better Customer Trust", icon: ShieldCheck },
                           { title: "Higher Social Reach", icon: Smartphone },
                           { title: "Strong Marketing Results", icon: Zap }
                        ].map((benefit, idx) => (
                           <div key={idx} className="p-6 rounded-xl bg-gradient-to-br from-[#0F0F16] to-[#0A0A12] border border-white/5 hover:border-purple-500/30 transition-colors">
                              <benefit.icon className="w-8 h-8 text-purple-400 mb-4" />
                              <h3 className="font-bold text-gray-200 text-lg">{benefit.title}</h3>
                           </div>
                        ))}
                     </div>
                  </div>

               </div>
            </div>
         </section>

         {/* 6. Industries We Serve */}
         <section className="py-24 bg-[#0A0A12] border-y border-white/5 relative z-10">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
               <h2 className="text-3xl md:text-4xl font-bold mb-16">Industries <span className="text-indigo-400">We Serve</span></h2>

               <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                  {[
                     { icon: HeartPulse, name: "Healthcare" },
                     { icon: GraduationCap, name: "Education" },
                     { icon: Utensils, name: "Hotels & Restaurants" },
                     { icon: Building2, name: "Real Estate" },
                     { icon: Store, name: "Retail Shops" },
                     { icon: ShoppingCart, name: "Fashion Brands" },
                     { icon: Factory, name: "Manufacturing" },
                     { icon: Lightbulb, name: "Startups" },
                     { icon: Cpu, name: "IT Companies" },
                     { icon: Stethoscope, name: "Hospitals" },
                     { icon: Dumbbell, name: "Gyms & Fitness" },
                     { icon: Briefcase, name: "Corporate Businesses" }
                  ].map((ind, idx) => (
                     <div key={idx} className="flex flex-col items-center justify-center p-8 bg-white/[0.02] border border-white/5 rounded-3xl hover:bg-indigo-500/10 hover:border-indigo-500/30 transition-all group">
                        <ind.icon className="w-12 h-12 text-gray-500 group-hover:text-indigo-400 transition-colors mb-4" />
                        <span className="font-bold text-gray-300 group-hover:text-white text-lg">{ind.name}</span>
                     </div>
                  ))}
               </div>
            </div>
         </section>

         {/* 7. Video Types We Create */}
         <section className="py-32 relative z-10">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
               <h2 className="text-4xl md:text-5xl font-bold mb-16">Video Types <span className="text-blue-400">We Create</span></h2>

               <div className="flex flex-wrap justify-center gap-4">
                  {[
                     "Promotional Videos", "Corporate Films", "Advertisement Videos",
                     "Product Showcase", "Brand Launch Videos", "Social Media Reels",
                     "Instagram Ads", "Facebook Ads", "YouTube Commercials",
                     "Documentary Style Videos", "Client Testimonials", "Motion Graphics Videos"
                  ].map((type, idx) => (
                     <div key={idx} className="px-6 py-3 bg-[#0F0F16] border border-white/10 rounded-full font-semibold text-gray-300 hover:bg-blue-600 hover:text-white hover:border-blue-600 transition-all cursor-pointer shadow-lg">
                        {type}
                     </div>
                  ))}
               </div>
            </div>
         </section>

         {/* 9. Portfolio Section */}
         <section className="py-32 bg-[#0A0A12] border-y border-white/5 relative z-10">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
               <div className="text-center mb-16">
                  <h2 className="text-4xl md:text-5xl font-bold mb-6">Our <span className="text-indigo-400">Portfolio</span></h2>
                  <p className="text-xl text-gray-400">A glimpse into our cinematic creations.</p>
               </div>

               <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {[
                     { title: "Corporate Videos", img: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?q=80&w=2070" },
                     { title: "Product Shoots", img: "https://images.unsplash.com/photo-1505740420928-5e560c06d30e?q=80&w=2070" },
                     { title: "Social Media Reels", img: "https://images.unsplash.com/photo-1611162617213-7d7a39e9b1d7?q=80&w=1974" },
                     { title: "Advertisements", img: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?q=80&w=2070" },
                     { title: "Drone Videos", img: "https://images.unsplash.com/photo-1473968512647-3e447244af8f?q=80&w=2070" },
                     { title: "Event Videos", img: "https://images.unsplash.com/photo-1511578314322-379afb476865?q=80&w=2069" }
                  ].map((item, idx) => (
                     <div key={idx} className="group relative h-64 md:h-80 rounded-3xl overflow-hidden border border-white/10 cursor-pointer">
                        <img src={item.img} alt={item.title} className="absolute inset-0 w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700" />
                        <div className="absolute inset-0 bg-black/50 group-hover:bg-black/30 transition-colors" />
                        <div className="absolute inset-0 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                           <PlayCircle className="w-16 h-16 text-white mb-4 drop-shadow-lg" />
                        </div>
                        <div className="absolute bottom-6 left-6 right-6">
                           <h3 className="text-xl font-bold text-white drop-shadow-md">{item.title}</h3>
                        </div>
                     </div>
                  ))}
               </div>
            </div>
         </section>

         {/* 10. FAQ */}
         <section className="py-32 relative z-10">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
               <div className="text-center mb-16">
                  <h2 className="text-4xl md:text-5xl font-bold mb-4">Frequently Asked <span className="text-blue-400">Questions</span></h2>
               </div>

               <div className="space-y-4">
                  {[
                     { q: "How much does a professional video cost?", a: "It depends on the project requirements including duration, locations, and editing complexity." },
                     { q: "Do you provide script writing?", a: "Yes, our creative team handles scriptwriting and concept development." },
                     { q: "Can you shoot in 4K?", a: "Yes, we shoot using high-end 4K cinema cameras for maximum quality." },
                     { q: "Do you edit existing videos?", a: "Yes, we offer professional video editing and motion graphics services for raw footage." },
                     { q: "Do you create Instagram & YouTube Reels?", a: "Absolutely. We specialize in engaging short-form content optimized for social media." },
                     { q: "How many days does it take?", a: "Usually 3–10 working days depending on the scope of the project." }
                  ].map((faq, idx) => (
                     <FAQItem key={idx} question={faq.q} answer={faq.a} />
                  ))}
               </div>
            </div>
         </section>


      </div>
   );
};

export default VideoProduction;
